import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { AuthSession } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────
// PROPOR UM CLUBE — tela única (design Forjamente Clubes, frame 2).
// Só pro fluxo de PROPOSTA. O "Criar um clube" normal continua no ClubCreateFlow.
// Reusa a estante (library) pro seletor de livros e carrega os mútuos pro convite.
// ─────────────────────────────────────────────────────────────────────────────

const mono: React.CSSProperties = { fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-3)' };
const EMBER_RGB = '232,122,59';
const HUES = [22, 8, 280, 150, 210];

export const ProporClubeForm: React.FC<{
  term?: 'clube' | 'grupo';
  session: AuthSession | null;
  currentUserId?: string;
  library?: any[];
  onBack: () => void;
  onSubmit: (data: any) => Promise<{ id: string } | undefined>;
  onCreated: (data: any, clubId: string | null) => void;
  onUploadCover?: (file: File) => Promise<string>;
}> = ({ term = 'clube', session, currentUserId, library = [], onBack, onSubmit, onCreated, onUploadCover }) => {
  const [name, setName] = useState('');
  const [coverHue, setCoverHue] = useState(22);
  const [customCover, setCustomCover] = useState<string | null>(null);
  const [objective, setObjective] = useState('');
  const [rulesText, setRulesText] = useState('');
  const [proposedStartAt, setProposedStartAt] = useState('');
  const [mode, setMode] = useState<'group-read' | 'shared-habit'>('group-read');
  const [postBy, setPostBy] = useState<'all' | 'leader-only'>('all');
  const [noDeadline, setNoDeadline] = useState(true);
  const [durationCount, setDurationCount] = useState(1);
  const [durationUnit, setDurationUnit] = useState<'semanas' | 'meses' | 'ano'>('meses');
  const [books, setBooks] = useState<any[]>([]);
  const [inviteMode, setInviteMode] = useState<'all' | 'select'>('all');
  const [mutuals, setMutuals] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bookSheet, setBookSheet] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // carrega mútuos (seguidores ∪ seguindo) pro seletor de convite
  useEffect(() => {
    (async () => {
      if (!currentUserId) return;
      try {
        const [fr, fg] = await Promise.all([
          api.listFollowers(session, currentUserId).catch(() => null),
          api.listFollowing(session, currentUserId).catch(() => null),
        ]);
        const pick = (r: any) => (r?.followers || r?.following || r?.users || r?.people || (Array.isArray(r) ? r : []) || []);
        const all = [...pick(fr), ...pick(fg)];
        const byId: any = {};
        all.forEach((p: any) => { if (p && p.id && p.id !== currentUserId) byId[p.id] = p; });
        setMutuals(Object.values(byId));
      } catch {}
    })();
  }, [currentUserId, session]);

  const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
  const canSubmit = name.trim().length > 0 && objective.trim().length > 0 && !!proposedStartAt;

  const doUpload = async (file: File) => {
    if (!onUploadCover || !file) return;
    try { const url = await onUploadCover(file); setCustomCover(url); } catch (e: any) { setError(e?.message || 'Erro no upload da capa'); }
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true); setError(null);
    const data = {
      propose: true,
      name: name.trim(),
      coverTemplate: customCover ? 'custom' : 'embers',
      customCover: customCover || null,
      customBanner: null,
      coverHue,
      description: '',
      mode,
      privacy: 'closed',
      postBy,
      storiesPostBy: postBy,
      objective: objective.trim(),
      rulesText: rulesText.trim() || null,
      proposedStartAt,
      totalBooksPlanned: Math.max(1, books.length),
      durationCount, durationUnit, noDeadline,
      sequenceBooks: books,
      startEmpty: books.length === 0,
      inviteMode,
      selectedIds: Array.from(selected),
    };
    try {
      const created = await onSubmit(data);
      if (created?.id) onCreated(data, created.id);
      else setError('Não foi possível criar a proposta.');
    } catch (e: any) {
      const msg = e?.message || '';
      setError(msg.includes('plan_limit') || msg.includes('plan_required') ? 'Você atingiu o limite de clubes do seu plano.' : 'Erro ao enviar a proposta.');
    } finally { setSubmitting(false); }
  };

  const addBook = (b: any) => { setBooks(prev => [...prev, b]); setBookSheet(false); };
  const removeBook = (i: number) => setBooks(prev => prev.filter((_, idx) => idx !== i));
  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      {/* header */}
      <div style={{ padding: '54px 20px 0', display: 'flex', alignItems: 'center', gap: 13 }}>
        <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--paper-2)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <svg width="9" height="15" viewBox="0 0 9 15" fill="none"><path d="m7.5 1.5-6 6 6 6" stroke="var(--ink)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div>
          <div className="serif" style={{ fontSize: 25, lineHeight: 1, color: 'var(--ink)' }}>Propor um {term}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>Monte o convite. Comece com o pé direito.</div>
        </div>
      </div>

      <div style={{ padding: '22px 20px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* OBJETIVO — coração */}
        <div style={{ background: `rgba(${EMBER_RGB},0.10)`, border: `1px solid var(--ember)`, borderRadius: 18, padding: 15 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-4.4-9.3-9C1.3 9.2 2.6 6 5.8 6 8 6 9.3 7.5 12 10.5 14.7 7.5 16 6 18.2 6c3.2 0 4.5 3.2 3.1 6-2.3 4.6-9.3 9-9.3 9Z" fill="var(--ember)" /></svg>
            <div style={{ ...mono, color: 'var(--ember-2)' }}>Objetivo · o coração do {term}</div>
          </div>
          <textarea value={objective} onChange={e => setObjective(e.target.value)} rows={4}
            placeholder="Descreva o que você quer com este clube: o estilo de leitura, o estilo de debate, o objetivo — pra todo mundo entrar em sintonia."
            style={{ width: '100%', marginTop: 11, boxSizing: 'border-box', resize: 'vertical', background: 'transparent', border: 'none', outline: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 16, lineHeight: 1.4 }} />
        </div>

        {/* NOME */}
        <div>
          <label style={mono}>Nome do {term}</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Travessias" maxLength={80}
            style={{ width: '100%', marginTop: 8, boxSizing: 'border-box', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '13px 14px', fontFamily: 'Instrument Serif, serif', fontSize: 18, color: 'var(--ink)', outline: 'none' }} />
        </div>

        {/* CAPA + LIVROS */}
        <div style={{ display: 'flex', gap: 13 }}>
          <div style={{ width: 84 }}>
            <label style={mono}>Capa</label>
            <button onClick={() => fileRef.current?.click()} style={{ marginTop: 8, width: 84, height: 112, borderRadius: 12, border: customCover ? 'none' : '1.5px dashed var(--line-strong)', overflow: 'hidden', background: customCover ? '#000' : `linear-gradient(155deg, hsl(${coverHue},45%,32%), hsl(${coverHue},50%,16%))`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', padding: 0 }}>
              {customCover ? <img src={customCover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="var(--ink-3)" strokeWidth="1.6" strokeLinecap="round" /></svg><span style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>Adicionar</span></>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); e.currentTarget.value = ''; }} />
            {/* tons rápidos */}
            <div style={{ display: 'flex', gap: 5, marginTop: 8, justifyContent: 'center' }}>
              {HUES.map(h => (
                <button key={h} onClick={() => { setCustomCover(null); setCoverHue(h); }} style={{ width: 16, height: 16, borderRadius: '50%', border: (!customCover && coverHue === h) ? '2px solid var(--ink)' : '1px solid var(--line)', background: `hsl(${h},50%,40%)`, cursor: 'pointer', padding: 0 }} />
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={mono}>Livros / sequência</label>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {books.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 11, padding: '9px 10px' }}>
                  <span style={{ fontSize: 10, fontFamily: 'Geist Mono, monospace', color: 'var(--ember)' }}>{i + 1}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</span>
                  <button onClick={() => removeBook(i)} style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              ))}
              <button onClick={() => setBookSheet(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 4px', fontSize: 11.5, color: 'var(--ember-2)', background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="var(--ember)" strokeWidth="1.8" strokeLinecap="round" /></svg> Adicionar livro
              </button>
            </div>
          </div>
        </div>

        {/* REGRAS */}
        <div>
          <label style={mono}>Regras (opcional)</label>
          <textarea value={rulesText} onChange={e => setRulesText(e.target.value)} rows={2}
            placeholder="Um marco por semana · sem spoilers além do marco · uma reflexão por marco."
            style={{ width: '100%', marginTop: 8, boxSizing: 'border-box', resize: 'vertical', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '13px 14px', fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)', outline: 'none' }} />
        </div>

        {/* DATA DE INÍCIO */}
        <div>
          <label style={mono}>Data de início <span style={{ color: 'var(--ember)' }}>*</span></label>
          <div style={{ display: 'flex', gap: 9, marginTop: 8 }}>
            <input type="datetime-local" value={proposedStartAt} onChange={e => setProposedStartAt(e.target.value)}
              style={{ flex: 1, minWidth: 0, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '13px 14px', color: 'var(--ink)', fontSize: 13.5, outline: 'none' }} />
            <button onClick={() => setProposedStartAt(nowLocal())} style={{ background: `rgba(${EMBER_RGB},0.14)`, border: '1px solid var(--ember)', borderRadius: 14, padding: '0 16px', color: 'var(--ember-2)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>HOJE</button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 7 }}>"Hoje" começa o {term} imediatamente quando você lançar.</div>
        </div>

        {/* QUEM CONVIDAR */}
        <div>
          <label style={mono}>Quem convidar</label>
          <div style={{ marginTop: 8, display: 'flex', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 13, padding: 4, gap: 4 }}>
            {(['all', 'select'] as const).map(m => (
              <button key={m} onClick={() => setInviteMode(m)} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: inviteMode === m ? 'var(--ember-soft)' : 'transparent', color: inviteMode === m ? 'var(--ember-2)' : 'var(--ink-3)' }}>
                {m === 'all' ? 'Todos meus mútuos' : 'Selecionar pessoas'}
              </button>
            ))}
          </div>
          {inviteMode === 'all' ? (
            <div style={{ marginTop: 11, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 13, padding: 13 }}>
              <div style={{ display: 'flex' }}>
                {mutuals.slice(0, 3).map((p, i) => <MiniAva key={p.id} p={p} style={{ marginLeft: i ? -9 : 0 }} />)}
                {mutuals.length > 3 && <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--paper-3)', border: '2px solid var(--paper)', marginLeft: -9, display: 'grid', placeItems: 'center', fontSize: 9, color: 'var(--ink-3)' }}>+{mutuals.length - 3}</div>}
              </div>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{mutuals.length} pessoas que você segue e que te seguem.</span>
            </div>
          ) : (
            <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 260, overflowY: 'auto' }}>
              {mutuals.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center', padding: 14 }}>Você ainda não tem mútuos pra convidar.</div>}
              {mutuals.map(p => {
                const on = selected.has(p.id);
                return (
                  <button key={p.id} onClick={() => toggleSelect(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 11, background: on ? 'var(--ember-soft)' : 'var(--paper-2)', border: `1px solid ${on ? 'var(--ember)' : 'var(--line)'}`, borderRadius: 12, padding: '9px 11px', cursor: 'pointer', textAlign: 'left' }}>
                    <MiniAva p={p} size={30} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name || p.display_name || 'Leitor'}</div>
                      {(p.username) && <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>@{p.username}</div>}
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: 6, border: on ? 'none' : '1.5px solid var(--line-strong)', background: on ? 'var(--ember)' : 'transparent', display: 'grid', placeItems: 'center' }}>
                      {on && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="m2 6 3 3 5-6" stroke="var(--paper)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* MAIS OPÇÕES (campos do criar normal) */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={mono}>Mais opções</div>
          <Choice label="Como vão ler?" value={mode} onChange={(v: any) => setMode(v)} options={[['group-read', 'Mesmo livro, juntos'], ['shared-habit', 'Cada um lê o seu']]} />
          <Choice label="Quem posta e discute?" value={postBy} onChange={(v: any) => setPostBy(v)} options={[['all', 'Todos participam'], ['leader-only', 'Só eu (líder)']]} />
          <div>
            <label style={mono}>Tem prazo?</label>
            <div style={{ display: 'flex', gap: 7, marginTop: 8 }}>
              <button onClick={() => setNoDeadline(true)} style={segStyle(noDeadline)}>No nosso tempo</button>
              <button onClick={() => setNoDeadline(false)} style={segStyle(!noDeadline)}>Com prazo</button>
            </div>
            {!noDeadline && (
              <div style={{ display: 'flex', gap: 8, marginTop: 9, alignItems: 'center' }}>
                <input type="number" min={1} value={durationCount} onChange={e => setDurationCount(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: 70, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: '10px', color: 'var(--ink)', fontSize: 14, textAlign: 'center', outline: 'none' }} />
                <select value={durationUnit} onChange={e => setDurationUnit(e.target.value as any)} style={{ flex: 1, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: '10px', color: 'var(--ink)', fontSize: 14, outline: 'none' }}>
                  <option value="semanas">semanas</option><option value="meses">meses</option><option value="ano">ano</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {error && <div style={{ background: `rgba(${EMBER_RGB},0.10)`, border: `1px solid rgba(${EMBER_RGB},0.3)`, borderRadius: 12, padding: '11px 14px', color: 'var(--ember)', fontSize: 13 }}>{error}</div>}
      </div>

      {/* botão fixo */}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, padding: '12px 20px 26px', maxWidth: 448, margin: '0 auto', background: 'linear-gradient(to top, var(--paper) 60%, rgba(0,0,0,0))' }}>
        <button onClick={submit} disabled={!canSubmit || submitting} style={{
          width: '100%', border: 'none', borderRadius: 16, padding: 16, fontSize: 15, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          background: canSubmit && !submitting ? 'var(--ember)' : 'var(--paper-3)',
          color: canSubmit && !submitting ? '#16110d' : 'var(--ink-4)',
          cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
          boxShadow: canSubmit && !submitting ? `0 14px 30px -10px rgba(${EMBER_RGB},0.6)` : 'none',
        }}>
          {submitting ? 'Enviando…' : <>Enviar convites · lançar {term}
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M18 2 9 11M18 2l-6 16-3-7-7-3 16-6Z" stroke="#16110d" strokeWidth="1.6" strokeLinejoin="round" /></svg></>}
        </button>
      </div>

      {bookSheet && <BookPickerSheet library={library} used={books} onClose={() => setBookSheet(false)} onAdd={addBook} />}
    </div>
  );
};

const MiniAva: React.FC<{ p: any; size?: number; style?: React.CSSProperties }> = ({ p, size = 26, style }) => {
  const url = p.avatar_url || p.avatarUrl || p.avatar;
  const initial = String(p.name || p.display_name || '?').charAt(0).toUpperCase();
  let h = 0; const seed = p.id || p.name || '?'; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return <div style={{ width: size, height: size, borderRadius: '50%', border: '2px solid var(--paper)', overflow: 'hidden', background: `linear-gradient(135deg, hsl(${h},42%,42%), hsl(${h},48%,26%))`, display: 'grid', placeItems: 'center', fontSize: size * 0.4, fontWeight: 600, color: '#fff', ...style }}>{typeof url === 'string' && url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}</div>;
};

const Choice: React.FC<any> = ({ label, value, onChange, options }) => (
  <div>
    <label style={mono}>{label}</label>
    <div style={{ display: 'flex', gap: 7, marginTop: 8 }}>
      {options.map(([v, l]: any) => (
        <button key={v} onClick={() => onChange(v)} style={segStyle(value === v)}>{l}</button>
      ))}
    </div>
  </div>
);

function segStyle(active: boolean): React.CSSProperties {
  return { flex: 1, padding: '11px 8px', borderRadius: 12, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? 'var(--ember)' : 'var(--line)'}`, background: active ? 'var(--ember-soft)' : 'var(--paper-2)', color: active ? 'var(--ember-2)' : 'var(--ink-3)' };
}

// Sheet de adicionar livro: escolher da estante OU digitar manual.
const BookPickerSheet: React.FC<{ library: any[]; used: any[]; onClose: () => void; onAdd: (b: any) => void }> = ({ library, used, onClose, onAdd }) => {
  const [manual, setManual] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState({ title: '', author: '', totalPages: '' });
  const usedIds = new Set(used.map(b => b.bookId).filter(Boolean));
  const filtered = library.filter((b: any) => !usedIds.has(b.id) && (`${b.title} ${b.author}`).toLowerCase().includes(q.toLowerCase().trim()));
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 448, margin: '0 auto', background: 'var(--paper)', borderRadius: '22px 22px 0 0', padding: 22, maxHeight: '78vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>Adicionar livro</div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--paper-2)', border: 'none', cursor: 'pointer', color: 'var(--ink)' }}>×</button>
        </div>
        {!manual ? (
          <>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar na sua estante…" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: '11px 14px', color: 'var(--ink)', fontSize: 14, outline: 'none', marginBottom: 12 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {filtered.map((b: any) => (
                <button key={b.id} onClick={() => onAdd({ title: b.title, author: b.author, totalPages: b.totalPages || b.pages || 1, coverHue: b.coverHue, coverUrl: b.coverUrl, bookId: b.id })}
                  style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 30, height: 44, borderRadius: 4, background: b.coverUrl ? '#000' : `linear-gradient(135deg, hsl(${b.coverHue || 22},45%,40%), hsl(${b.coverHue || 22},50%,22%))`, overflow: 'hidden', flex: 'none' }}>{b.coverUrl && <img src={b.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}><div className="serif" style={{ fontSize: 14, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{b.author}</div></div>
                </button>
              ))}
              {filtered.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: 12 }}>Nenhum livro na estante{q ? ' com esse nome' : ''}.</div>}
            </div>
            <button onClick={() => setManual(true)} style={{ width: '100%', marginTop: 12, padding: 12, borderRadius: 12, background: 'transparent', border: '1px dashed var(--line-strong)', color: 'var(--ember-2)', fontSize: 13, cursor: 'pointer' }}>+ Adicionar um livro que não está aqui</button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Título" style={inp} />
            <input value={draft.author} onChange={e => setDraft({ ...draft, author: e.target.value })} placeholder="Autor" style={inp} />
            <input type="number" value={draft.totalPages} onChange={e => setDraft({ ...draft, totalPages: e.target.value })} placeholder="Total de páginas" style={inp} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setManual(false)} style={{ flex: 1, padding: 12, borderRadius: 12, background: 'var(--paper-2)', border: 'none', color: 'var(--ink-2)', cursor: 'pointer' }}>Voltar</button>
              <button onClick={() => { const p = parseInt(draft.totalPages, 10); if (draft.title.trim() && draft.author.trim() && p > 0) onAdd({ title: draft.title.trim(), author: draft.author.trim(), totalPages: p, coverHue: 22 }); }}
                style={{ flex: 1, padding: 12, borderRadius: 12, background: 'var(--ember)', border: 'none', color: '#16110d', fontWeight: 600, cursor: 'pointer' }}>Adicionar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px', color: 'var(--ink)', fontSize: 14, outline: 'none' };
